import React from 'react';
import LanguageIcon from '@mui/icons-material/Language';
import WorkIcon from '@mui/icons-material/Work';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import BuildIcon from '@mui/icons-material/Build';
import FlagIcon from '@mui/icons-material/Flag';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import ChangeHistoryOutlinedIcon from '@mui/icons-material/ChangeHistoryOutlined';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import PersonIcon from '@mui/icons-material/Person';
import FaceIcon from '@mui/icons-material/Face';
import {
    SessionQuestionEntityFrontend,
    QuestionTypeFrontend,
    AnswerTypeFrontend,
    LogActorTypeFrontend,
    LogActionFrontend,
    QuestionOptionDtoFrontend,
    ListItemFieldDefinitionFrontend,
} from '../../../api/sessionQuestionsApi';
import { parseFlexibleDate } from '../../../utils/dateUtils';

// Category display order
export const categoryOrder: QuestionTypeFrontend[] = [
    QuestionTypeFrontend.LANGUAGE_SELECTION,
    QuestionTypeFrontend.PROFESSION_SELECTION,
    QuestionTypeFrontend.PERSONAL,
    QuestionTypeFrontend.PROFESSION,
    QuestionTypeFrontend.ACTION,
];

// Category display config
export const categoryConfig: Record<QuestionTypeFrontend, { name: string; icon: React.ReactNode; color: string }> = {
    [QuestionTypeFrontend.LANGUAGE_SELECTION]: { name: "Language Choice", icon: React.createElement(LanguageIcon), color: "#2196f3" },
    [QuestionTypeFrontend.PROFESSION_SELECTION]: { name: "Profession Choice", icon: React.createElement(WorkIcon), color: "#4caf50" },
    [QuestionTypeFrontend.FINAL_PROFESSION_CONFIRMATION]: { name: "Profession Confirmation", icon: React.createElement(RateReviewOutlinedIcon), color: "#4caf50" },
    [QuestionTypeFrontend.CUSTOM_OCCUPATION_INPUT]: { name: "Custom Occupation", icon: React.createElement(WorkIcon), color: "#4caf50" },
    [QuestionTypeFrontend.PERSONAL]: { name: "Personal Questions", icon: React.createElement(PersonOutlineIcon), color: "#ff9800" },
    [QuestionTypeFrontend.PROFESSION]: { name: "Profession Questions", icon: React.createElement(BuildIcon), color: "#9c27b0" },
    [QuestionTypeFrontend.CV_UPLOAD]: { name: "CV Upload", icon: React.createElement(DescriptionIcon), color: "#607d8b" },
    [QuestionTypeFrontend.ACTION]: { name: "Actions & Other", icon: React.createElement(FlagIcon), color: "#f44336" },
    [QuestionTypeFrontend.CLOSING]: { name: "Closing", icon: React.createElement(CheckCircleIcon), color: "#4caf50" },
};

export const getQuestionLabel = (question: SessionQuestionEntityFrontend): string => {
    const override = question.questionName?.trim();
    if (override) return override;
    return question.questionShorten || question.question;
};

// Convert an answer value to a string for draft comparison and form values.
// Per PR1 worker-as-truth redesign, the answer is no longer on the session_question
// itself — callers project it from the worker via `getAnswerForQuestion(q, worker)`
// and pass it in here.
export const getAnswerAsString = (
    _question: SessionQuestionEntityFrontend,
    answer: unknown,
): string => {
    if (answer === null || answer === undefined) return '';
    if (typeof answer === 'string') return answer;
    if (typeof answer === 'boolean') return String(answer);
    if (typeof answer === 'number') return String(answer);
    return JSON.stringify(answer);
};

// Human-readable display of an answer value. Answer is supplied by the caller
// (typically `getAnswerForQuestion(question, worker)` from sessionQuestionsApi).
export const getDisplayAnswer = (
    question: SessionQuestionEntityFrontend,
    answer: unknown,
): string => {
    const { answerType, options, listItemSchema } = question;
    if (answer === null || answer === undefined) return 'Not answered';

    switch (answerType) {
        case AnswerTypeFrontend.BOOLEAN:
            if (typeof answer === 'boolean') return answer ? 'Yes' : 'No';
            if (typeof answer === 'string') return answer.toLowerCase() === 'true' ? 'Yes' : 'No';
            return 'N/A';
        case AnswerTypeFrontend.DATE:
            if (typeof answer === 'string') {
                try {
                    const parsedDate = parseFlexibleDate(answer);
                    if (parsedDate) return parsedDate.toLocaleDateString();
                    return 'Invalid Date';
                } catch { return 'Invalid Date'; }
            }
            return 'Invalid Date';
        case AnswerTypeFrontend.FILE:
            if (typeof answer === 'string') return answer.trim() !== '' ? 'File Uploaded' : 'No File';
            return 'No File';
        case AnswerTypeFrontend.LIST_OBJECT:
            if (Array.isArray(answer) && listItemSchema) {
                if (answer.length === 0) return 'No items entered.';
                return answer.map((item, index) => {
                    const itemFields = listItemSchema.map((def: ListItemFieldDefinitionFrontend) => {
                        const value = item[def.fieldName];
                        const displayValue = typeof value === 'boolean' ? (value ? 'Yes' : 'No') : (value ?? 'N/A');
                        return `${def.label}: ${displayValue}`;
                    }).join('; ');
                    return `Item ${index + 1}: { ${itemFields} }`;
                }).join(' | ');
            }
            return 'N/A';
        case AnswerTypeFrontend.RADIO:
            if (typeof answer === 'string' && options) {
                const opt = options.find((o: QuestionOptionDtoFrontend) => o.key === answer);
                return opt ? opt.label : answer;
            }
            return typeof answer === 'string' ? answer : JSON.stringify(answer);
        case AnswerTypeFrontend.MULTISELECT:
            if (Array.isArray(answer) && options) {
                return answer.map(key => {
                    if (typeof key !== 'string') return JSON.stringify(key);
                    const opt = options.find((o: QuestionOptionDtoFrontend) => o.key === key);
                    return opt ? opt.label : key;
                }).join(', ') || 'N/A';
            } else if (typeof answer === 'string' && options) {
                const opt = options.find((o: QuestionOptionDtoFrontend) => o.key === answer);
                return opt ? opt.label : answer;
            }
            return Array.isArray(answer) ? JSON.stringify(answer) : (typeof answer === 'string' ? answer : 'N/A');
        case AnswerTypeFrontend.TEXT:
        case AnswerTypeFrontend.TEXT_AREA:
        case AnswerTypeFrontend.NUMBER:
            if (typeof answer === 'string') return answer;
            if (typeof answer === 'number') return String(answer);
            return JSON.stringify(answer);
        default:
            if (typeof answer === 'string') return answer;
            if (typeof answer === 'number') return String(answer);
            return JSON.stringify(answer);
    }
};

// Check if a question has been answered. Post PR1 (worker-as-truth), the canonical
// signal is `question.answeredAt` (a timestamp). For callers that still inspect
// answer values directly, pass them in explicitly.
export const isAnswered = (question: SessionQuestionEntityFrontend, answer?: unknown): boolean => {
    if (answer !== undefined) {
        if (answer === null) return false;
        if (typeof answer === 'string') return answer.trim() !== '';
        if (Array.isArray(answer)) return answer.length > 0;
        return true;
    }
    // Default: rely on the session_question metadata flag.
    return question.answeredAt != null;
};

// Sort questions by category order, then questionNumber, then questionId
export const sortQuestions = (data: SessionQuestionEntityFrontend[]): SessionQuestionEntityFrontend[] => {
    return [...data].sort((a, b) => {
        const aIdx = categoryOrder.indexOf(a.questionType);
        const bIdx = categoryOrder.indexOf(b.questionType);
        const effectiveA = aIdx === -1 ? Infinity : aIdx;
        const effectiveB = bIdx === -1 ? Infinity : bIdx;
        if (effectiveA !== effectiveB) return effectiveA - effectiveB;
        const aNum = (a as any).questionNumber ?? 0;
        const bNum = (b as any).questionNumber ?? 0;
        if (aNum !== bNum) return aNum - bNum;
        return a.questionId - b.questionId;
    });
};

// History modal helpers
export const getLogActionVisuals = (action: LogActionFrontend) => {
    switch (action) {
        case LogActionFrontend.CREATED:
            return { icon: React.createElement(AddCircleOutlineIcon, { fontSize: 'small', sx: { mr: 0.5 } }), color: 'success.main' };
        case LogActionFrontend.ANSWERED:
            return { icon: React.createElement(RateReviewOutlinedIcon, { fontSize: 'small', sx: { mr: 0.5 } }), color: 'info.main' };
        case LogActionFrontend.UPDATED:
            return { icon: React.createElement(ChangeHistoryOutlinedIcon, { fontSize: 'small', sx: { mr: 0.5 } }), color: 'warning.main' };
        default:
            return { icon: null, color: 'text.secondary' };
    }
};

export const getActorFriendlyName = (actorType: LogActorTypeFrontend, actorId?: string | null) => {
    switch (actorType) {
        case LogActorTypeFrontend.AI:
            return { name: 'AI Process', icon: React.createElement(SmartToyIcon, { fontSize: 'small', sx: { mr: 0.5 } }) };
        case LogActorTypeFrontend.SYSTEM:
            return { name: 'System Process', icon: React.createElement(SystemUpdateAltIcon, { fontSize: 'small', sx: { mr: 0.5 } }) };
        case LogActorTypeFrontend.USER:
            return { name: `Admin/User ${actorId ? `(${actorId})` : ''}`, icon: React.createElement(PersonIcon, { fontSize: 'small', sx: { mr: 0.5 } }) };
        case LogActorTypeFrontend.WORKER:
            return { name: `Worker ${actorId ? `(${actorId})` : ''}`, icon: React.createElement(FaceIcon, { fontSize: 'small', sx: { mr: 0.5 } }) };
        default:
            return { name: 'Unknown Actor', icon: null };
    }
};

export const formatLogValue = (value: any): string => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'object') {
        if (value.hasOwnProperty('answer') && value.hasOwnProperty('answeredAt')) {
            return `Answer: "${value.answer}" (at: ${value.answeredAt ? new Date(value.answeredAt).toLocaleString() : 'N/A'})`;
        }
        if (value.hasOwnProperty('question') && value.hasOwnProperty('questionDataKey')) {
            return `Question: "${value.question}" (key: ${value.questionDataKey || 'N/A'})`;
        }
        try { return JSON.stringify(value, null, 2); } catch { return 'Unreadable Object'; }
    }
    return String(value);
};
